import BackgroundHue from '@atoms/BackgroundHue/BackgroundHue';
import Button from '@atoms/Button/Button';
import Image from '@atoms/Image/Image';
import MotionWrapper from '@atoms/MotionWrapper/MotionWrapper';
import GlowColourEnum from '@models/enums/GlowColourEnum';
import type { IImage } from '@models/IImage';
import type { ILink } from '@models/ILink';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import useReducedMotion from '@utils/hooks/useReducedMotion';
import { useSpringString } from '@utils/hooks/useSpring';
import { useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';

interface Props {
  readonly anchorId?: string;
  readonly bottomImage: IImage;
  readonly link: ILink;
  readonly title: string;
  readonly topImage: IImage;
}

const ParallaxHero = ({
  anchorId = '',
  bottomImage,
  link,
  title,
  topImage,
}: Props) => {
  const prefersReducedMotion = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    offset: ['start end', 'end start'],
    target: ref,
  });
  const { scrollYProgress: pageScrollYProgress } = useScroll();
  const isMobileView = useMediaQuery(767);

  const y1 = useTransform(
    scrollYProgress,
    [0, 1],
    isMobileView ? [-50, 50] : [-150, 150]
  );
  const y2 = useTransform(
    scrollYProgress,
    [0, 1],
    isMobileView ? [25, -75] : [100, -250]
  );
  const scale = useTransform(scrollYProgress, [0, 1], [1.1, 0.9]);

  const transform = useTransform(pageScrollYProgress, [0, 1], ['0%', '-100%']);
  const physics = {
    damping: 15,
    mass: 0.27,
    stiffness: 55,
  };
  const spring = useSpringString(transform, physics);

  return (
    <section
      className="flex min-h-[50vh] overflow-hidden bg-black-100 py-15 text-white lg:min-h-[95vh]"
      id={`a-${anchorId}`}
      ref={ref}
      data-testid="CT-04-E-Parallax-Hero"
    >
      <MotionWrapper
        className="container flex flex-col items-stretch lg:grid lg:grid-cols-12 lg:items-center"
        initial={{
          opacity: 0,
          y: prefersReducedMotion ? '0px' : 50,
        }}
        style={{ y: prefersReducedMotion ? '0px' : spring }}
        viewport={{ once: true }}
        whileInView={{
          opacity: 1,
          y: '0px',
        }}
        data-testid="CT-04-E-Parallax-Hero-Container"
      >
        <div className="grow md:col-span-6 md:col-start-1 xl:col-span-5">
          <h1
            dangerouslySetInnerHTML={{ __html: title }}
            data-testid="CT-04-E-Parallax-Hero-Title"
          />

          {link?.url && link?.name && (
            <Button
              className="mt-11 text-center lg:mt-16"
              link={link}
              dataTestId="CT-04-E-Parallax-Hero-Link"
            />
          )}
        </div>

        <div className="relative flex grow items-center pt-16 md:col-span-7 md:col-start-7 lg:pt-0 xl:col-span-7 xl:col-start-6">
          <MotionWrapper
            className="pointer-events-none absolute top-0 left-0 z-10 flex w-full items-center justify-center md:-top-16"
            style={{ y: prefersReducedMotion ? '0px' : y1 }}
          >
            <BackgroundHue
              className="h-full w-full scale-x-[200%] scale-y-150"
              colour={GlowColourEnum.YellowFaint}
              dataTestId="CT-04-E-Parallax-Hero-Hue"
            />
          </MotionWrapper>

          <MotionWrapper
            className="relative z-20 w-full"
            style={{
              scale: prefersReducedMotion ? 1 : scale,
              y: prefersReducedMotion ? '0px' : y1,
            }}
          >
            <Image
              image={topImage}
              imageClasses="h-full w-full object-contain"
              sizes="(min-width: 1024px) 1024px, 100vw"
              dataTestId="CT-04-E-Parallax-Hero-Top-Image"
            />
          </MotionWrapper>

          <MotionWrapper
            className="absolute z-10 w-full"
            style={{
              scale: prefersReducedMotion ? 1 : scale,
              y: prefersReducedMotion ? '0px' : y2,
            }}
          >
            <Image
              image={bottomImage}
              imageClasses="h-full w-full object-contain"
              sizes="(min-width: 1024px) 1024px, 100vw"
              dataTestId="CT-04-E-Parallax-Hero-Bottom-Image"
            />
          </MotionWrapper>
        </div>
      </MotionWrapper>
    </section>
  );
};

export default ParallaxHero;
